import { Heading, HStack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { type ReactElement, useState } from 'react'

import { isNotNull } from '@/utils/helpers'
import parseQueryNumber from '@/utils/parseQueryNumber'

import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import TopicQ from '@/queries/TopicQ'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import type { Query } from '@/client/query'
import {
  DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS,
  QUERY_REFLECTION_SESSION_ID,
} from '@/client/services/dialogues/constants'
import { isDialogueSeries } from '@/client/services/dialogues/helpers'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { getPositionInSeries } from '@/client/services/session'
import { updateQueryParams } from '@/client/services/url/helpers'

import DialoguesReflectionModal from '@/components/Dialogues/DialoguesReflectionModal'
import ExcusedAbsenceModal from '@/components/Series/ExcusedAbsenceModal'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'

import SeriesAttendancePolicy from '../../SeriesAttendancePolicy'
import SessionCardList from './internal/SessionCardList'

type ScheduleTabs = 'Upcoming' | 'All' | 'Action Required'

export default function Sessions({
  series,
  isUserParticipant,
  isUserRegisteredLearner,
}: {
  series: Query
  isUserParticipant: boolean
  isUserRegisteredLearner: boolean
}): ReactElement {
  const user = useUser()
  const router = useRouter()
  const now = useServerNow()

  const querySessionId = router.query[QUERY_REFLECTION_SESSION_ID]
  const selectedSessionId =
    querySessionId != null ? parseQueryNumber({ value: querySessionId, defaultValue: 0 }) : null

  const seriesHasEnded = series(SeriesQ.isClosed()) ?? false

  const seriesTemplateSlug = series(SeriesQ.getSeriesTemplateSlug()) ?? ''
  const isDialogue = isDialogueSeries(seriesTemplateSlug)

  const allSessions = series
    .many(
      SeriesQ.getSessions({
        include: [{ association: 'Topic' }],
      })
    )
    .map(session => {
      const id = session(SessionQ.id) ?? 0
      const hasEnded = session(SessionQ.hasEnded()) ?? false

      const topic = session.one(SessionQ.getTopic())

      const mainSessionRegistration = session.one(
        SessionRegistrationQ.where({
          userId: user.id,
          sessionId: SessionQ.id,
          sessionBreakoutId: null,
        })
      )

      const breakoutSessionRegistrationIds = session
        .many(
          SessionRegistrationQ.where({
            userId: user.id,
            sessionId: SessionQ.id,
            sessionBreakoutId: true,
            attemptedJoining: true,
            paranoid: false,
          })
        )
        .map(breakoutSessionRegistration => breakoutSessionRegistration(SessionRegistrationQ.id))

      const excusedAbsenceSource = mainSessionRegistration(
        SessionRegistrationQ.excused_absence_source
      )
      const hasExcusedAbsence = excusedAbsenceSource != null
      const didAttemptSession =
        mainSessionRegistration(SessionRegistrationQ.attempted_joining) ?? false
      const didOrWillAttend = hasEnded ? didAttemptSession : !hasExcusedAbsence

      const attemptedBreakout = breakoutSessionRegistrationIds.length > 0

      const learnerFeedbacks = session.many(
        LearnerFeedbackQ.where({
          sessionId: SessionQ.id,
          reviewerUserId: user.id,
        })
      )
      const reflections = learnerFeedbacks
        .map(learnerFeedback => {
          const learnerFeedbackId = learnerFeedback(LearnerFeedbackQ.id) ?? 0
          const reviewee_user_id = learnerFeedback(LearnerFeedbackQ.reviewee_user_id) ?? 0
          const reviewer_user_id = learnerFeedback(LearnerFeedbackQ.reviewer_user_id) ?? 0
          const type = learnerFeedback(LearnerFeedbackQ.type)

          if (type == null) {
            return null
          }
          return {
            id: learnerFeedbackId,
            reviewee_user_id,
            reviewer_user_id,
            type,
          }
        })
        .filter(isNotNull)

      return {
        id,
        isUpcoming: !hasEnded,
        positionInSeries: getPositionInSeries(session) ?? 0,
        startDate: DateTime.fromISO(session(SessionQ.start_date) ?? ''),
        endDate: DateTime.fromISO(session(SessionQ.end_date) ?? ''),
        title: session(SessionQ.getDisplayTitle()) ?? '',
        topicName: topic(TopicQ.name) ?? '',
        description: session(SessionQ.description) ?? EMPTY_RICH_TEXT_VALUE,
        attendancePolicy: session(SessionQ.attendance_policy) ?? 'optional',
        excusedAbsenceSource,
        didOrWillAttend,
        didAttemptSession,
        attemptedBreakout,
        reflections,
      }
    })

  const actionRequiredSessions = isDialogue
    ? allSessions.filter(session => {
        const isExpired =
          session.endDate < now.minus({ days: DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS })
        return (
          !session.isUpcoming &&
          session.attemptedBreakout &&
          session.reflections.length === 0 &&
          !isExpired
        )
      })
    : []

  const handleOpenReflectionModal = async (sessionId: number) => {
    await router.push(
      updateQueryParams({
        params: [{ key: QUERY_REFLECTION_SESSION_ID, value: String(sessionId) }],
        router,
      })
    )
  }

  const handleCloseReflectionModal = async () => {
    await router.replace(
      updateQueryParams({
        params: [{ key: QUERY_REFLECTION_SESSION_ID, value: null }],
        router,
      })
    )
  }

  const [currentTab, tabToUrl] = useUrlTabController<ScheduleTabs>({
    queryKey: 'show',
    tabToQueryParam: {
      Upcoming: 'upcoming',
      'Action Required': 'action',
      All: 'all',
    },
    defaultTab: isDialogue && actionRequiredSessions.length > 0 ? 'Action Required' : 'Upcoming',
  })

  const [excusedAbsenceModalSessionId, setExcusedAbsenceModalSessionId] = useState<number | null>(
    null
  )

  const sessions = seriesHasEnded
    ? allSessions
    : currentTab === 'Upcoming'
      ? allSessions.filter(s => s.isUpcoming)
      : currentTab === 'Action Required'
        ? actionRequiredSessions
        : allSessions

  const attendancePolicy = series(SeriesQ.attendance_policy) ?? ''
  const hasRequiredAttendanceSessions = series(SeriesQ.hasRequiredAttendanceSessions()) ?? false

  const isSATSeries = series(SeriesQ.isSATSeries()) ?? false

  const attendancePolicyComponent = !isUserParticipant ? (
    <SeriesAttendancePolicy
      attendancePolicy={attendancePolicy}
      hasRequiredAttendanceSessions={hasRequiredAttendanceSessions}
      isSATBootcamp={isSATSeries}
    />
  ) : null

  return (
    <>
      <DialoguesReflectionModal
        isOpen={selectedSessionId != null}
        onClose={handleCloseReflectionModal}
      />
      <ExcusedAbsenceModal
        upcomingSessions={sessions.filter(({ isUpcoming }) => isUpcoming)}
        isOpen={excusedAbsenceModalSessionId != null}
        onClose={() => setExcusedAbsenceModalSessionId(null)}
        preSelectedSessionId={excusedAbsenceModalSessionId}
      />
      <HStack alignItems="center" justifyContent="space-between">
        <Heading size="xl">Sessions</Heading>
        {seriesHasEnded ? null : (
          <Tabs<ScheduleTabs>
            currentTab={currentTab}
            tabs={
              isDialogue && isUserParticipant
                ? ['Action Required', 'Upcoming', 'All']
                : ['Upcoming', 'All']
            }
            tabToUrl={tabToUrl}
            hideBorder
          />
        )}
      </HStack>
      {attendancePolicyComponent}
      {sessions.length > 0 ? (
        <SessionCardList
          sessions={sessions}
          isUserRegisteredLearner={isUserRegisteredLearner}
          openExcusedAbsenceModal={(sessionId?: number) =>
            setExcusedAbsenceModalSessionId(sessionId ?? null)
          }
          openReflectionModal={async (sessionId: number) => {
            await handleOpenReflectionModal(sessionId)
          }}
          isDialogue={isDialogue}
        />
      ) : (
        <Text>No sessions found.</Text>
      )}
    </>
  )
}
